if (!customElements.get('collection-showcase')) {
  class CollectionShowcase extends HTMLElement {
    connectedCallback() {
      if (this.dataset.layout !== 'slider') return;

      this.track = this.querySelector('[data-cs-track]');
      this.prevButton = this.querySelector('[data-cs-prev]');
      this.nextButton = this.querySelector('[data-cs-next]');
      this.dotsContainer = this.querySelector('[data-cs-dots]');
      if (!this.track) return;

      this.onPrev = () => this.scrollByPage(-1);
      this.onNext = () => this.scrollByPage(1);
      this.onScroll = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      };
      this.onBlockSelect = (event) => this.scrollToBlock(event.target);

      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.resizeObserver = new ResizeObserver(() => {
        this.measureSides();
        this.fitEnd();
        this.buildDots();
        this.update();
      });
      this.resizeObserver.observe(this.track);

      // Don't wait for the observer: measure right away so the end spacing
      // and arrows are correct from the first swipe.
      this.measureSides();
      this.fitEnd();
      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      this.track.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    // Adds empty space after the last card so the final scroll position starts
    // exactly on a card. Without it, a partially visible "next card" makes the
    // slider stop at the very end with the first visible card cut off.
    // Distance from the carousel area to each screen edge. On mobile the track
    // extends exactly this much so cards slide flush to the edge of the screen,
    // whatever side padding the page container has.
    measureSides() {
      const body = this.track.parentElement.getBoundingClientRect();
      const screenWidth = document.documentElement.clientWidth;
      const left = Math.max(0, Math.round(body.left));
      const right = Math.max(0, Math.round(screenWidth - body.right));
      this.style.setProperty('--cs-side-left', `${left}px`);
      this.style.setProperty('--cs-side-right', `${right}px`);
    }

    fitEnd() {
      const items = Array.from(this.track.children).filter((item) => item.offsetWidth > 0);
      this.track.style.setProperty('--cs-end-extra', '0px');
      if (items.length < 2) return;

      const firstStyle = getComputedStyle(items[0]);
      // Scroll origin = start of the track content (before the first item's offset).
      const origin = items[0].offsetLeft - parseFloat(firstStyle.marginLeft);
      // On mobile the spacing is each item's left padding: keep the same space after the last card.
      const trailing = parseFloat(getComputedStyle(items[items.length - 1]).paddingLeft) || 0;
      const viewport = this.track.clientWidth;
      const last = items[items.length - 1];
      const contentEnd = last.offsetLeft + last.offsetWidth - origin;
      if (contentEnd + trailing <= viewport + 1) return;

      // First resting position (an item start) from which every remaining card fits.
      const snaps = items.map((item, index) => (index === 0 ? 0 : item.offsetLeft - origin));
      const lastStart = snaps.find((start) => contentEnd - start + trailing <= viewport + 1);
      const extra = Math.max(0, lastStart + viewport - contentEnd);
      this.track.style.setProperty('--cs-end-extra', `${Math.floor(extra)}px`);
    }

    get maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    get pageCount() {
      if (this.maxScroll <= 1) return 1;
      return Math.ceil(this.track.scrollWidth / this.track.clientWidth - 0.01);
    }

    scrollByPage(direction) {
      this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: 'smooth' });
    }

    scrollToBlock(target) {
      if (!this.contains(target)) return;
      const item = target.closest('.cs-item');
      if (!item) return;
      this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }

    buildDots() {
      if (!this.dotsContainer) return;
      const count = this.pageCount;
      if (this.dotsContainer.childElementCount === count) return;

      this.dotsContainer.innerHTML = '';
      if (count <= 1) return;

      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cs-dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => {
          this.track.scrollTo({ left: Math.min(i * this.track.clientWidth, this.maxScroll), behavior: 'smooth' });
        });
        this.dotsContainer.appendChild(dot);
      }
    }

    update() {
      const max = this.maxScroll;
      const left = Math.abs(this.track.scrollLeft);
      const hasOverflow = max > 1;

      this.toggleAttribute('data-no-overflow', !hasOverflow);
      if (this.prevButton) this.prevButton.disabled = left <= 1;
      if (this.nextButton) this.nextButton.disabled = left >= max - 1;

      if (this.dotsContainer) {
        const dots = this.dotsContainer.children;
        const active = left >= max - 1 ? dots.length - 1 : Math.round(left / this.track.clientWidth);
        Array.from(dots).forEach((dot, index) => {
          dot.setAttribute('aria-current', index === active ? 'true' : 'false');
        });
      }
    }
  }

  customElements.define('collection-showcase', CollectionShowcase);
}
